/**
 * The "Delete a vault by hand" guide's provider-note table: the same
 * `vaultDeleteProviderNote` table the desktop app's Delete dialog will read,
 * laid out as a page so app and site cannot say different things about the
 * same provider.
 *
 * Those sentences describe **the app's own Delete**, and this table sits at
 * the foot of a guide about deleting by hand — the one place they are not
 * interchangeable. Read unlabelled, three rows flatly contradict the section
 * they link to: the app's Drive delete really does bypass Trash, but the
 * guide's Drive section tells you to go empty the Trash; the app really does
 * delete every S3 version, but the guide's S3 section explains that a delete
 * by name leaves them all. So the heading and the lead-in say whose delete
 * this is, and the column is "What the app leaves behind", not a bare
 * "Recoverability" that reads as a claim about whatever the reader just did.
 *
 * One row per case the shared table actually distinguishes, not one per
 * provider the app supports — AWS and GCS get a single "standard class" row
 * because `vaultDeleteProviderNote` gives them the same note, and a second
 * row repeating it would just be the same sentence shown twice. Azure is not
 * in that row: the app reaches it over its own Blob API rather than as an S3
 * bucket (ADR 0023), and its note says what soft delete keeps, so its Hot
 * tier has a row of its own and every Azure row links to the guide's Azure
 * section.
 *
 * Since #413, "the same note" stops being true once a class carries a
 * retention floor: a vault now records its own provider and class (#411),
 * and `vaultDeleteProviderNote`'s fourth argument names that class and its
 * floor instead of the generic "other storage classes may bill" hedge. Every
 * such class is a case this table distinguishes, so `FLOOR_CLASS_ROWS` below
 * walks the shared catalogue (`storageClasses.ts`) for one row per
 * floor-bearing AWS, GCS or Azure class — hand-listing them here would drift
 * from the catalogue the same way a hand-copied constant always does. Wasabi
 * is walked past: its one class is the standard-class row's own case, already
 * a dedicated row above, and its sentence already reads this way (it was the
 * one row the hedge never applied to before this ticket named the rest).
 * Backblaze B2's one class never carries a floor, so it never appears here
 * either — its row above is unaffected by any of this.
 *
 * The one case it does not distinguish is the delete's scope, which the
 * shared table takes as an optional third argument and the Drive row reads
 * (#224). A page has no vault in front of it and so no scope to pass: these
 * rows are read by someone deciding, not by someone whose vault is already
 * classified. So no row passes one, and Drive's falls back to the attached
 * sentence — why that fallback is the safe one is argued at the parameter
 * itself, in `vaultDeleteProviderNote`.
 *
 * Falling back is not the same as being complete, and the lead-in carries the
 * difference. Every vault the app set up for itself is owned —
 * `create_storage_location` writes `container_created = Some(true)`
 * unconditionally, and only Attach writes `false` — so plenty of real Drive
 * vaults get the *other* of Drive's two sentences in the dialog, and this row
 * is the one place the page and the app do not match. Leaving that to be found
 * by opening the app would be #224's own failure moved onto the site: two
 * descriptions of one delete, disagreeing, with nothing saying which applies.
 * So the lead-in names the other sentence and the vaults that get it. The row
 * itself is unchanged — still the sentence a scope-less caller gets, and still
 * the one that matches the by-hand guide this table sits under.
 *
 * It says "made the folder for you" rather than naming the button that does it.
 * "Back up my files…" usually creates, but it also offers to attach when it
 * finds a vault already at the place the reader chose, so the button does not
 * decide the scope and naming it here would be wrong for those readers. Which
 * of the two happened is the thing they can always tell.
 */

import {
  STORAGE_CLASSES,
  type DestinationKind,
  type VaultStorageTier,
  vaultDeleteProviderNote,
  vaultProviderName,
} from "@bigbear/shared";

interface Row {
  readonly label: string;
  readonly kind: DestinationKind;
  /** The provider (and class) a vault would have recorded (#411), which is
   * what picks an S3 row (#611). Absent for a row with no vault provider. */
  readonly storageTier?: VaultStorageTier;
}

/**
 * One row per AWS, GCS or Azure class the catalogue gives a retention floor —
 * see the module comment for why these are walked off the catalogue rather
 * than hand-listed, and why Wasabi and Backblaze B2 are not walked here.
 */
const FLOOR_CLASS_ROWS: Row[] = STORAGE_CLASSES.filter(
  (storageClass): storageClass is typeof storageClass & { provider: "aws" | "gcs" | "azure" } =>
    storageClass.retentionFloorDays !== null &&
    (storageClass.provider === "aws" || storageClass.provider === "gcs" || storageClass.provider === "azure"),
).map((storageClass) => ({
  label: `${vaultProviderName(storageClass.provider)} — ${storageClass.displayName}`,
  kind: "s3" as const,
  storageTier: { provider: storageClass.provider, storageClass: storageClass.requestValue },
}));

const ROWS: Row[] = [
  { label: "A drive or network share", kind: "local" },
  { label: "Google Drive", kind: "google-drive" },
  { label: "OneDrive", kind: "onedrive" },
  { label: "Dropbox", kind: "dropbox" },
  { label: "Backblaze B2", kind: "s3", storageTier: { provider: "backblaze", storageClass: null } },
  { label: "Wasabi", kind: "s3", storageTier: { provider: "wasabi", storageClass: null } },
  {
    label: "AWS S3 or Google Cloud Storage (standard class)",
    kind: "s3",
    storageTier: { provider: "aws", storageClass: "STANDARD" },
  },
  // Its own row, labelled the way the floor rows below label Azure's other
  // tiers: the app reaches Azure over its own Blob API (ADR 0023), so it shares
  // neither the AWS and GCS sentence nor their guide section.
  { label: "Azure Blob Storage — Hot tier", kind: "s3", storageTier: { provider: "azure", storageClass: "Hot" } },
  ...FLOOR_CLASS_ROWS,
  { label: "Another S3-compatible provider", kind: "s3", storageTier: { provider: "other", storageClass: null } },
];

export function DeleteVaultProviderNoteTable() {
  return (
    <section className="panel" id="provider-notes" data-testid="provider-note-table">
      <h2>What the app&rsquo;s own Delete costs and leaves behind</h2>
      <p>
        These are the notes the app shows in its <strong>Delete</strong> dialog, for when you can
        still reach the vault from the PC that set it up. Deleting by hand at the provider is not
        the same thing and does not always leave the same result — each row links to the section
        above that says what to do there instead.
      </p>
      {/* The one row whose sentence depends on the vault rather than the
          provider, and this page has no vault in front of it. Spelled out here
          rather than split into two Drive rows: the reader of this page is
          deciding, and two rows would make them classify their own vault before
          they could read either. */}
      <p>
        The Google Drive row below is what the app says about a folder you pointed it at yourself.
        Where Big Bear Backup made the folder for you instead, its <strong>Delete</strong> removes
        that folder as well, and anything you had moved to Drive&rsquo;s bin from inside it goes for
        good with it. The app&rsquo;s dialog says so on those vaults.
      </p>
      <table>
        <thead>
          <tr>
            <th scope="col">Provider</th>
            <th scope="col">Billing</th>
            <th scope="col">What the app leaves behind</th>
          </tr>
        </thead>
        <tbody>
          {ROWS.map((row) => {
            const note = vaultDeleteProviderNote(row.kind, undefined, row.storageTier);
            return (
              <tr key={row.label}>
                <td>
                  <a href={`#${note.guideAnchor}`}>{row.label}</a>
                </td>
                <td>{note.retention}</td>
                <td>{note.recoverability}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}
